import { NgOptimizedImage } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { EventsService } from '../../../events/data-access/events.service';
import { PaymentService } from '../../../payment/data-access/payment.service';
import { HeaderComponent } from '../../ui/header/header.component';
import { SeatSelectionState } from '../../../seat-selection/data-access/seat-selection.state';
import { MapViewerNode } from '@3ddv/dvm-internal';
import { Subscription } from 'rxjs';
import { SectionsService } from '../../data-access/sections.service';
import { SectionViewerService } from '../../data-access/dvm/section-viewer.service';
import { SeatViewerService } from '../../data-access/dvm/seat-viewer.service';

/**
 * @description A component that acts as a wrapper for the header, facilitating navigation based on the current route.
 * This component includes logic to determine the action when the navigation arrow is clicked,
 * navigating to different parts of the event-related routes.
 *
 * @example
 * // Include this component in a parent template to provide a consistent header across different views.
 * <app-header-wrapper></app-header-wrapper>
 *
 * @see HeaderComponent
 */
@Component({
  selector: 'app-header-wrapper',
  imports: [HeaderComponent],
  templateUrl: './header-wrapper.component.html',
})
export class HeaderWrapperComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private seatSelectionState = inject(SeatSelectionState);
  private sectionsService = inject(SectionsService);
  private sectionViewerService = inject(SectionViewerService);
  private seatViewerService = inject(SeatViewerService);
  public eventService = inject(EventsService);
  public paymentService = inject(PaymentService);

  protected mapNode!: MapViewerNode;
  protected subscription!: Subscription;

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.subscription = this.sectionsService.selectedSectionData$.subscribe(selectedSection => {
      if (!selectedSection) {
        return;
      }

      this.mapNode = this.sectionViewerService.viewer2DRef?.service.getNodeById(
        selectedSection?.sectionID.toString()
      ) as MapViewerNode;
    });
  }
  /**
   * Handles navigation based on the current route when the arrow button is clicked.
   *
   * - Navigates to different routes depending on the current route.
   * - If the current route is `/checkout`, it cancels the ongoing checkout process before navigating.
   * - Logs an error if the current route is unhandled.
   */
  public onArrowClick() {
    switch (this.router.url) {
      case '/events/tickets': {
        this.router.navigate(['/events']);
        break;
      }
      case '/seat-selection': {
        if (this.seatSelectionState.getState() === '3d-view') {
          this.seatViewerService.handleClickOnSeatMapEnabled(this.mapNode, this.sectionViewerService.viewer2DRef!);
        } else {
          this.router.navigate(['/events/tickets']);
        }
        break;
      }
      case '/checkout': {
        this.paymentService.onCheckoutCanceled();
        this.router.navigate(['/seat-selection']);
        break;
      }
      case '/payment': {
        this.router.navigate(['/checkout']);
        break;
      }
      default: {
        console.error(`Unhandled route: ${this.router.url}`);
        break;
      }
    }
  }
  /**
   * Retrieves the base path of the current route, excluding any query parameters.
   *
   * - Splits the URL at the `?` symbol and returns only the base path.
   *
   * @returns  The base URL path without query parameters.
   */
  public getRouteValue = () => {
    return this.router.url.split('?')[0];
  };
}
